import { type Till } from "~/api/tills/types";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";

type TillType = Till["type"];
type TillForType<T extends TillType> = Extract<Till, { type: T }>;

type ConfigItemProps<T extends TillType> = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  description?: React.ReactNode;
  allowedTillTypes: T[];
  hidden?: boolean;
  children: (till: TillForType<T>) => React.ReactNode;
};

export const ConfigItem = <T extends TillType>({
  title,
  subtitle,
  description,
  allowedTillTypes,
  hidden,
  children
}: ConfigItemProps<T>) => {
  const { data: till } = useCurrentTill();

  if (
    !till ||
    hidden ||
    (allowedTillTypes.length > 0 && !allowedTillTypes.includes(till.type as T))
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <p className="text-2xl font-bold">{title}</p>

        {subtitle ? (
          <p className="text-base text-text-secondary">{subtitle}</p>
        ) : null}
      </div>

      <div className="flex h-[72px] items-center justify-between rounded-md bg-background-secondary px-5">
        {description ? (
          <p className="text-base text-text-primary">{description}</p>
        ) : null}

        {children(till as TillForType<T>)}
      </div>
    </div>
  );
};
